<script setup lang="ts">
import { useI18n } from '@/i18n';
import { markCheckoutCelebration } from '@/lib/celebrateCheckout';
import { canSkipPaymentForInvoice } from '@/lib/checkoutEligibility';
import { logError } from '@/lib/logError';
import { trackSubscribeConversion } from '@/lib/marketing';
import rest from '@/lib/rest';
import { toltReferralBody } from '@/lib/tolt';
import { useBillingStore } from '@/stores/billing';
import { usePaymentStore } from '@/stores/payment';
import { useUserStore } from '@/stores/user';
import type { CurrencySwitchPreview, PlanProduct } from '@/types/billing';
import { computeTrialEndSeconds, useBillingUtils } from '@/use/billingUtils';
import { useToast } from '@/use/toast';
import { Button, useConfirmStore } from '@dabble/quillui';
import type { Stripe } from 'stripe';
import { computed, onMounted, ref, useTemplateRef } from 'vue';
import { useRouter } from 'vue-router';
import CheckoutFrame from '@/components/CheckoutFrame/CheckoutFrame.vue';
import BillingInvoiceSummary from '@/components/BillingInvoiceSummary/BillingInvoiceSummary.vue';
import BillingCouponApplyButton from '@/components/BillingCouponApplyButton/BillingCouponApplyButton.vue';
import BillingCouponPlanNotice from '@/components/BillingCouponPlanNotice/BillingCouponPlanNotice.vue';
import BillingStripePaymentForm from '@/components/BillingStripePaymentForm/BillingStripePaymentForm.vue';

const { t } = useI18n();
const billingStore = useBillingStore();
const paymentStore = usePaymentStore();
const userStore = useUserStore();
const confirmStore = useConfirmStore();
const { showToast } = useToast();
const { formatPrice, formatUnixDate } = useBillingUtils();
const router = useRouter();

const paymentForm = useTemplateRef<InstanceType<typeof BillingStripePaymentForm>>('paymentForm');

const upcomingInvoice = ref<Stripe.Invoice | null>(null);
const recurringInvoice = ref<Stripe.Invoice | null>(null);
const isLoadingInvoice = ref(false);
// Neither payment branch renders until the first preview is in: mounting the Stripe form
// creates a SetupIntent, and a $0 plan change would then unmount it straight away.
const hasLoadedInvoice = ref(false);
const isActivating = ref(false);

// What POST /billing/checkout/activate returns: done, or a first payment the customer
// has to authenticate before the server will call the subscription live.
interface ActivateCheckoutResult {
  success: boolean;
  requiresAction?: { clientSecret: string; subscriptionId: string };
}

// Set once a bank challenge has succeeded and only the server's confirmation is
// outstanding. At that point the money has moved, so a retry must finish THAT
// subscription — re-running activate would bill a second one (DAB-1403).
const pendingCompletion = ref<string | null>(null);

// Have the server confirm with Stripe that the authenticated subscription is genuinely
// paid. It throws on anything short of that; a soft `success: false` is a refusal too.
async function completeCheckout(subscriptionId: string): Promise<void> {
  const result = (await rest
    .post('/billing/checkout/complete')
    .header('x-uid', userStore.user?.uid || '')
    .credentials('include')
    .send({ subscriptionId })) as ActivateCheckoutResult | null | undefined;
  if (result?.success === false) throw new Error('CHECKOUT_INCOMPLETE');
}

// Once the server has refused the no-card change, a card is required again under the
// ordinary rules — not "optional", and Confirm must not resend the same request.
const canSkipPayment = computed(() =>
  canSkipPaymentForInvoice(upcomingInvoice.value, {
    isPlanChange: isPlanChangeCheckout.value && !needsPaymentForm.value,
  }),
);

// Stripe's deeply recursive types (Invoice → TaxId → Customer → Account → …) don't
// always unify across module boundaries; the cast lets us pass the same shape
// across a component prop without TS bailing on the recursion depth.
type StripeInvoice = Stripe.Invoice;
const upcomingInvoiceForSummary = computed<StripeInvoice | null>(
  () => upcomingInvoice.value as unknown as StripeInvoice | null,
);
const recurringInvoiceForSummary = computed<StripeInvoice | null>(
  () => recurringInvoice.value as unknown as StripeInvoice | null,
);

const planData = computed(() => billingStore.selectedPlan);
const planName = computed(() => billingStore.product?.name ?? '');
const planAmount = computed(() => planData.value?.amount ?? 0);
const planCurrency = computed(() => planData.value?.currency ?? 'usd');
const planInterval = computed(() => planData.value?.interval ?? 'month');

const isChangingPlan = computed(() => billingStore.isActivePaid);

const productId = computed(() => billingStore.product?.id);
const couponInterval = computed(() => (billingStore.selection === 'month' ? 'month' : 'year'));
const isLifetimeCheckout = computed(() => billingStore.selection === 'lifetime');
const isLifetimeUpgrade = computed(() => billingStore.isLifetimeUpgradeCheckout);

// An upgrade reads as the lifetime it buys, from the one the customer already owns.
const summaryPlanName = computed(() =>
  isLifetimeUpgrade.value ? t('billing_plan_name_lifetime', { name: planName.value }) : planName.value,
);
const summaryPlanDetail = computed(() => {
  const owned = billingStore.primarySubscription?.plan;
  const name = owned?.name || owned?.product?.name;
  return isLifetimeUpgrade.value && name ? t('checkout_lifetime_upgrade_from', { name }) : undefined;
});
// A Lifetime gift card redeemed for the plan it pays for: no card, and nothing charged.
const isGiftCheckout = computed(() => Boolean(billingStore.effectiveCheckoutCoupon?.lifetimePrice));
// Something else on the account would be billed with the gift (dabble-rest refuses it the same way).
const giftLeavesAmountDue = computed(() => isGiftCheckout.value && (upcomingInvoice.value?.amount_due ?? 0) > 0);
// Redeeming ends the plan the account is on now, without credit for its unused time.
const giftEndsPlan = computed(() => isGiftCheckout.value && Boolean(billingStore.primarySubscription));

// A code for another plan: move checkout onto that plan, same interval, and re-price.
function switchToCouponPlan(plan: PlanProduct): void {
  if (billingStore.selectPlanForCheckout(plan, couponInterval.value)) void loadUpcomingInvoice();
}

// Trial end date for this checkout — the date the first real charge lands, and the
// gate for the "your card will not be charged until {date}" note. Shared with
// BillingInvoiceSummary via computeTrialEndSeconds so an ordinary credit-covered
// upgrade is never mislabelled as a trial (DAB-754).
const trialEndSeconds = computed<number | null>(() =>
  computeTrialEndSeconds({
    subscription: billingStore.primarySubscription,
    upcomingInvoice: upcomingInvoice.value,
    recurringInvoice: recurringInvoice.value,
    planAmount: planAmount.value,
  }),
);

const trialEndDate = computed(() => formatUnixDate(trialEndSeconds.value));

function getReturnUrl(): string {
  return new URL(router.currentRoute.value.fullPath, window.location.origin).href;
}

// A paying customer selected a currency different from the one their live
// subscription is billed in — checkout will migrate them (cancel + FX-credit +
// recreate) rather than a simple plan change.
const isCurrencySwitch = computed(() => billingStore.checkoutSwitchesCurrency);

// A plan change on a live, active subscription in its own currency. When one nets
// $0 due (a downgrade covered by proration credit) there is nothing to collect, so
// no payment form at all — Stripe still raises the $0 invoice to record the change
// (DAB-957). Not while a cancel is pending: the change would lift it, and un-cancelling
// re-arms a renewal that needs a card (DAB-1288; the server applies the same rule).
const hasPendingCancel = computed(() => {
  const sub = billingStore.primarySubscription;
  return Boolean(sub?.cancel_at_period_end || sub?.cancel_at);
});
// A lifetime upgrade is a purchase, not a change to the subscription, and dabble-rest charges it to
// a card on file, so it never goes without one.
const isPlanChangeCheckout = computed(
  () => isChangingPlan.value && !isCurrencySwitch.value && !hasPendingCancel.value && !isLifetimeUpgrade.value,
);
// Set when the server refuses the no-card activation (its view of the subscription can
// differ from ours), so the payment form comes back and a card can be added.
const needsPaymentForm = ref(false);
const isPaymentNotNeeded = computed(
  () => isGiftCheckout.value || (isPlanChangeCheckout.value && canSkipPayment.value && !needsPaymentForm.value),
);

function formatMoney(amount: number, currency: string): string {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency.toUpperCase() }).format(amount / 100);
}

// Spell out the consequences of a currency switch before we touch the account:
// the unused-time credit we'll grant, what they pay now, and any lost discount.
async function confirmCurrencySwitch(planId: string): Promise<boolean> {
  let preview: CurrencySwitchPreview;
  try {
    preview = await billingStore.previewCurrencySwitch(planId);
  } catch (err) {
    logError('Failed to preview currency switch:', err);
    showToast('danger', checkoutErrorMessage(err, 'billing_currency_switch_error'));
    return false;
  }
  // A trial is converted by the same checkout, so it says so and offers to keep the trial instead.
  const isTrial = billingStore.isTrialing;
  const parts = [t('billing_currency_switch_intro', { currency: preview.currency.toUpperCase() })];
  if (isTrial) parts.push(t('billing_currency_switch_trial_ends'));
  if (preview.credit > 0) {
    parts.push(t('billing_currency_switch_credit', { credit: formatMoney(preview.credit, preview.currency) }));
  }
  parts.push(t('billing_currency_switch_charge', { net: formatMoney(preview.netDue, preview.currency) }));
  if (preview.losesDiscount) parts.push(t('billing_currency_switch_discount_loss'));
  return confirmStore.confirm(t('billing_currency_switch_title'), parts.join(' '), {
    cancel: { text: t(isTrial ? 'billing_currency_switch_cancel_trial' : 'billing_currency_switch_cancel') },
    confirm: { text: t('billing_currency_switch_confirm') },
  });
}

async function activatePlan(): Promise<void> {
  const plan = billingStore.selectedPlan;
  if (!plan) return;

  // Only a first-time activation is an acquisition. Existing paid subscribers
  // reach this same choke point via plan-change / currency-switch flows, so
  // capture subscription state up front and skip the conversion for them. A gift
  // card's redemption is no sale either: the buyer's checkout was.
  const isNewSubscription = !billingStore.hasActiveSubscription && !isGiftCheckout.value;
  const celebration = isLifetimeUpgrade.value ? 'upgrade' : billingStore.selection;

  // The Tolt click id goes up with the activation so dabble-rest can stamp it on
  // the Stripe subscription. That metadata is the whole of the affiliate's claim to
  // the sale, and this is the last request before the subscription exists.
  if (pendingCompletion.value) {
    // A previous attempt's challenge already succeeded and the charge went through;
    // only /complete failed. Finish that subscription instead of creating another.
    await completeCheckout(pendingCompletion.value);
  } else {
    const body: {
      planId: string;
      currency?: string;
      coupon?: string;
      tolt_referral?: string;
      handlesRequiresAction: boolean;
      switchCurrency?: boolean;
    } = {
      planId: plan.id,
      // This build can run the bank's challenge (below). Saying so is what lets the
      // server hand a `requiresAction` back; a tab loaded before this shipped never
      // sends it and keeps getting the old error instead of a reply it would mistake
      // for success.
      handlesRequiresAction: true,
      ...(await toltReferralBody()),
    };
    if (billingStore.currency) body.currency = billingStore.currency;
    // The customer confirmed the switch dialog (onSubscribe stops otherwise). dabble-rest only
    // cancels and recreates a sub in a new currency when told so, never on the currency alone (DAB-1996).
    if (isCurrencySwitch.value) body.switchCurrency = true;
    // A new subscriber's checkout coupon is applied as the subscription is created,
    // so the very first invoice is already discounted (DAB-848).
    if (billingStore.effectiveCheckoutCoupon) body.coupon = billingStore.effectiveCheckoutCoupon.id;
    const activation = (await rest
      .post('/billing/checkout/activate')
      .header('x-uid', userStore.user?.uid || '')
      .credentials('include')
      .send(body)) as ActivateCheckoutResult | null | undefined;
    // Stripe couldn't take the first payment without the customer: 3DS under SCA, or
    // the authentication that registers an RBI e-mandate on an India-issued card. Run
    // the bank's challenge here, then have the server confirm with Stripe that the
    // subscription is genuinely paid before anything below treats it as done (DAB-1403).
    if (activation?.requiresAction) {
      await paymentStore.handleNextAction(activation.requiresAction.clientSecret);
      // Past this line the charge has been authenticated, so pin the subscription a
      // retry has to finish before asking the server to settle it.
      pendingCompletion.value = activation.requiresAction.subscriptionId;
      await completeCheckout(activation.requiresAction.subscriptionId);
    } else if (activation?.success === false) {
      // The server reported a refusal in the body rather than as an HTTP error. Nothing
      // below may run: it tracks a conversion and celebrates a subscription.
      throw new Error('CHECKOUT_INCOMPLETE');
    }
  }
  pendingCompletion.value = null;
  billingStore.clearCheckoutCoupon();
  // Subscribe conversion (GA4 purchase + subscribe + Ads, FB Subscribe,
  // Pinterest checkout). This is the single completion choke point (payment form
  // `on-confirmed` + skip-payment path). Report the actual first charge (0 for a
  // 100%-off coupon) rather than the recurring list price, so ROAS isn't
  // overstated. Stripe amounts are in cents. The activate endpoint returns no
  // invoice id, so the transaction id is synthesized — unique per activation,
  // which is all GA needs to dedupe a re-fired event. No-op off production / in
  // impersonated tabs.
  if (isNewSubscription) {
    const chargedCents = upcomingInvoice.value?.amount_due ?? plan.amount ?? 0;
    trackSubscribeConversion({
      value: chargedCents / 100,
      currency: plan.currency ?? 'usd',
      transactionId: `${userStore.user?.uid ?? 'anon'}-${Date.now()}`,
      planId: plan.id,
      planName: planName.value,
    });
  }
  markCheckoutCelebration(celebration);
  router.push('/billing/portal');
}

/** A refusal the server names (a discount yearly billing can't take, say), else `fallbackKey`'s copy. */
function checkoutErrorMessage(err: unknown, fallbackKey: string): string {
  return billingStore.translateCheckoutError(err instanceof Error ? err.message : '') ?? t(fallbackKey);
}

async function activateWithoutPayment(): Promise<void> {
  if (isActivating.value) return;
  isActivating.value = true;
  try {
    await activatePlan();
  } catch (err) {
    // Surface the failure — otherwise a coupon-driven $0 checkout that the server
    // rejects (e.g. no payment method, stale state) looks like Subscribe did nothing
    // (RyanHavoc review).
    logError('Failed to activate plan without payment:', err);
    showToast('danger', checkoutErrorMessage(err, 'checkout_activate_error'));
    needsPaymentForm.value = true;
  } finally {
    isActivating.value = false;
  }
}

async function loadUpcomingInvoice(): Promise<void> {
  if (!billingStore.selectedPlan) return;
  isLoadingInvoice.value = true;
  try {
    // Thread a checkout-stashed coupon (new subscriber, no sub to attach to) into
    // the preview so the order summary reflects the discount (DAB-848).
    const body: { plan: string; currency?: string; discounts?: { coupon: string }[] } = {
      plan: billingStore.selectedPlan.id,
    };
    if (billingStore.currency) body.currency = billingStore.currency;
    if (billingStore.effectiveCheckoutCoupon) body.discounts = [{ coupon: billingStore.effectiveCheckoutCoupon.id }];
    const result = (await rest
      .post('/billing/invoices/upcoming')
      .header('x-uid', userStore.user?.uid || '')
      .credentials('include')
      .send(body)) as {
      next: Stripe.Invoice | null;
      recurring: Stripe.Invoice | null;
    } | null;
    upcomingInvoice.value = result?.next ?? null;
    recurringInvoice.value = result?.recurring ?? null;
  } catch (err) {
    // The previous total stays on screen — so say so. Swallowing this is what let a
    // coupon report "applied successfully" while the summary silently kept showing the
    // undiscounted price, with nothing to tell the user the figure was stale (DAB-996).
    logError('Failed to load upcoming invoice:', err);
    showToast('danger', checkoutErrorMessage(err, 'checkout_summary_refresh_error'));
  } finally {
    isLoadingInvoice.value = false;
    hasLoadedInvoice.value = true;
  }
}

onMounted(() => {
  if (!billingStore.selectedPlan) {
    router.replace('/billing/plans');
    return;
  }
  void loadUpcomingInvoice();
});

const subscribeDisabled = computed(() => {
  if (isLoadingInvoice.value) return true;
  if (isGiftCheckout.value) return isActivating.value || giftLeavesAmountDue.value;
  const f = paymentForm.value;
  // When nothing is due, a card is optional — never block on the payment form.
  if (canSkipPayment.value) return isActivating.value || (f?.isSubmitting ?? false);
  if (!f) return true;
  return f.isSubmitting || f.isInitializing || !f.isPaymentComplete;
});

const subscribeLoading = computed(() => {
  const f = paymentForm.value;
  if (canSkipPayment.value || isGiftCheckout.value) return isActivating.value || (f?.isSubmitting ?? false);
  return f?.isSubmitting ?? false;
});

const subscribeLabel = computed(() => {
  if (subscribeLoading.value) return t('checkout_processing');
  if (isLifetimeUpgrade.value) {
    const invoice = upcomingInvoice.value;
    const total = formatPrice(invoice?.amount_due ?? planAmount.value, invoice?.currency ?? planCurrency.value);
    return t('checkout_pay_and_upgrade', { total });
  }
  if (isGiftCheckout.value) return t('checkout_gift_redeem');
  return isChangingPlan.value ? t('checkout_confirm_change') : t('checkout_subscribe_now');
});

const noPaymentKey = computed(() => {
  if (!isGiftCheckout.value) return 'checkout_plan_change_nothing_due';
  return giftLeavesAmountDue.value ? 'billing_error_LIFETIME_GIFT_AMOUNT_DUE' : 'checkout_gift_nothing_due';
});

// Confirm a currency switch BEFORE we set up payment — cancelling the dialog
// after the card SetupIntent already ran would save the card yet abort the
// switch, leaving the flow half-done.
async function onSubscribe(): Promise<void> {
  const plan = billingStore.selectedPlan;
  if (!plan) return;
  if (isCurrencySwitch.value && !isGiftCheckout.value && !(await confirmCurrencySwitch(plan.id))) return;
  if (isPaymentNotNeeded.value) {
    void activateWithoutPayment();
    return;
  }
  const f = paymentForm.value;
  // Nothing due and no card entered → activate straight away. If they DID add a
  // card, submit it so it's saved for the next renewal.
  if (canSkipPayment.value && !f?.isPaymentComplete) {
    void activateWithoutPayment();
    return;
  }
  void f?.submit();
}
</script>

<template>
  <CheckoutFrame
    :back-to="{ name: isLifetimeUpgrade ? 'portal' : 'billing-plans' }"
    :back-label="t(isLifetimeUpgrade ? 'checkout_back_to_billing' : 'checkout_back_to_plans')"
  >
    <header class="checkout__header">
      <h1 class="checkout__title">{{ t('checkout_title') }}</h1>
    </header>

    <div class="checkout__grid">
      <div class="checkout__aside">
        <section class="checkout__summary">
          <BillingInvoiceSummary
            :upcoming-invoice="upcomingInvoiceForSummary"
            :recurring-invoice="recurringInvoiceForSummary"
            :is-loading-invoice="isLoadingInvoice"
            :plan-name="summaryPlanName"
            :plan-detail="summaryPlanDetail"
            :plan-amount="planAmount"
            :plan-currency="planCurrency"
            :plan-interval="planInterval"
          />
          <p v-if="isGiftCheckout" class="checkout__coupon checkout__coupon-note">
            {{ t('checkout_gift_applied') }}
          </p>
          <template v-else>
            <p v-if="isLifetimeCheckout" class="checkout__coupon checkout__coupon-note">
              {{ t('checkout_lifetime_coupon_note') }}
            </p>
            <!-- A gift card pays for a whole lifetime, never for an owner's upgrade. -->
            <div v-if="!isLifetimeUpgrade" :class="isLifetimeCheckout ? 'checkout__gift-entry' : 'checkout__coupon'">
              <BillingCouponApplyButton
                link
                :gift="isLifetimeCheckout"
                :disabled="isLoadingInvoice"
                :product-id="productId"
                :interval="isLifetimeCheckout ? 'lifetime' : couponInterval"
                @applied="loadUpcomingInvoice"
              />
            </div>
            <BillingCouponPlanNotice
              v-if="!isLifetimeCheckout || billingStore.checkoutCouponRefusal"
              class="checkout__coupon-plan"
              :product-id="productId"
              :plan-name="planName"
              :interval="couponInterval"
              :disabled="isLoadingInvoice || subscribeLoading"
              @switch="switchToCouponPlan"
              @change="router.push({ name: 'billing-plans' })"
              @keep="router.push('/billing/portal')"
            />
          </template>
        </section>

        <div class="checkout__action">
          <Button variant="primary" size="large" fill-width :disabled="subscribeDisabled" @click="onSubscribe">
            {{ subscribeLabel }}
          </Button>

          <p v-if="giftEndsPlan" class="checkout__note">{{ t('checkout_gift_plan_ends') }}</p>
          <p v-else-if="trialEndDate && !isGiftCheckout" class="checkout__note">
            {{ t('checkout_due_not_charged_until', { date: trialEndDate }) }}
          </p>
          <p class="checkout__secure">{{ t('checkout_secured_by_stripe') }}</p>
        </div>
      </div>

      <section v-if="!hasLoadedInvoice" class="checkout__payment" />
      <section v-else-if="isPaymentNotNeeded" class="checkout__payment">
        <p class="checkout__no-payment">{{ t(noPaymentKey) }}</p>
      </section>
      <section v-else class="checkout__payment">
        <BillingStripePaymentForm
          ref="paymentForm"
          :get-return-url="getReturnUrl"
          :on-confirmed="activatePlan"
          :on-address-saved="loadUpcomingInvoice"
        />
        <p v-if="canSkipPayment" class="checkout__optional">{{ t('checkout_payment_optional') }}</p>
      </section>
    </div>
  </CheckoutFrame>
</template>

<style lang="scss" scoped>
.checkout__header {
  text-align: center;
  margin-bottom: space('large');
}

.checkout__title {
  margin: 0;
  font-family: var(--font-family-ui-serif, ui-serif, Georgia, serif);
  font-size: fontSize('xlarge');
  font-weight: fontWeight('bold');
  letter-spacing: -0.01em;
  color: theme('color-text-primary');
}

.checkout__grid {
  display: grid;
  // Summary is compact; give the payment column the extra width so Stripe's
  // fields (card row, city/state/postal) reflow instead of stacking tall.
  grid-template-columns: minmax(0, 22rem) minmax(0, 1fr);
  gap: space('large');
  width: 100%;
  max-width: 56rem;
  align-items: start;
}

// Summary + the primary CTA travel together in the left column, kept in view as
// the payment column scrolls. `align-items: start` above lets sticky engage.
.checkout__aside {
  position: sticky;
  top: space('large');
  display: flex;
  flex-direction: column;
  gap: space('large');
  min-width: 0;
}

.checkout__summary,
.checkout__payment {
  padding: space('large');
  background-color: theme('color-fg-app');
  border: 1px solid theme('color-border-primary');
  border-radius: borderRadius('medium');
  box-shadow: elevation('small');
}

.checkout__no-payment {
  margin: 0;
  color: theme('color-text-secondary');
}

.checkout__optional {
  margin: space('small') 0 0;
  font-size: fontSize('small');
  color: theme('color-text-secondary');
}

.checkout__coupon {
  margin-top: space('small');
  padding-top: space('small');
  border-top: 1px solid theme('color-border-secondary');
}

.checkout__coupon-note {
  margin-bottom: 0;
  font-size: fontSize('small');
  color: theme('color-text-secondary');
}

.checkout__coupon-plan {
  margin-top: space('small');
}

.checkout__gift-entry {
  margin-top: space('xsmall');
}

.checkout__action {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: space('small');
}

.checkout__note {
  margin: 0;
  text-align: center;
  font-size: fontSize('small');
  color: theme('color-text-secondary');
}

.checkout__secure {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: space('tiny');
  margin: 0;
  font-size: fontSize('xsmall');
  color: theme('color-text-placeholder');
}

@media (max-width: 720px) {
  .checkout__grid {
    grid-template-columns: minmax(0, 1fr);
    max-width: 30rem;
  }

  // Flatten the aside so summary / payment / CTA stack in a natural top-to-bottom
  // order (summary first, then the form, then the button at the end).
  .checkout__aside {
    display: contents;
  }

  .checkout__summary {
    order: 1;
  }

  .checkout__payment {
    order: 2;
  }

  .checkout__action {
    order: 3;
  }
}
</style>
